// src/pages/league-dashboard/salary-contract/components/PriceEditor.js
import React, { useState } from 'react';
import { Edit, Users, Search } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { DynastyTable } from '../../../../services/tableService';
import { createPricePreviewColumns } from '../../../../services/tableService';

const PriceEditor = ({
  generatedPrices,
  customPrices,
  settings,
  viewMode,
  setViewMode,
  positionFilter,
  setPositionFilter,
  searchFilter,
  setSearchFilter,
  bulkEditMode,
  setBulkEditMode,
  selectedPlayers,
  setSelectedPlayers,
  setPriceAdjustmentModal,
  handleManualPriceChange,
  getFilteredPrices,
  positions,
  leagueId,
  league,
}) => {
  // State for year selection (stats_season vs current_season)
  const [selectedYear, setSelectedYear] = useState('previous');

  // Determine current and stats season years
  const currentSeasonYear = league?.current_season;
  const previousSeasonYear = league?.stats_season || (currentSeasonYear - 1);

  const columns = createPricePreviewColumns({
    bulkEditMode,
    selectedPlayers,
    setSelectedPlayers,
    handleManualPriceChange,
    settings,
    viewMode,
    leagueId,
    getFilteredPrices,
    selectedYear, // Pass selected year selector ('current' or 'previous')
    currentSeasonYear, // Pass actual year numbers for array lookup
    previousSeasonYear
  });

  // Handle case where no prices exist yet
  if (!generatedPrices?.prices) {
    return null;
  }

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${dynastyTheme.components.heading.h3}`}>
            <Edit className="inline w-5 h-5 mr-2" />
            Player Price Editor
          </h3>
          
          {/* Bulk Edit Controls */}
          <div className="flex gap-2">
            <button
              onClick={() => setBulkEditMode(!bulkEditMode)}
              className={bulkEditMode 
                ? dynastyTheme.utils.getComponent('button', 'warning', 'sm')
                : dynastyTheme.utils.getComponent('button', 'secondary', 'sm')
              }
            >
              <Users className="w-4 h-4 mr-2" />
              {bulkEditMode ? 'Cancel Bulk Edit' : 'Bulk Edit'}
            </button>
            
            {bulkEditMode && selectedPlayers.size > 0 && (
              <button
                onClick={() => setPriceAdjustmentModal(true)}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
              >
                <Edit className="w-4 h-4 mr-2" />
                Adjust {selectedPlayers.size} Players
              </button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex gap-4 mb-4">
          <div className="flex border rounded-lg">
            <button
              onClick={() => {
                setViewMode('hitting');
                setPositionFilter('all');
              }}
              className={`px-4 py-2 ${
                viewMode === 'hitting'
                  ? dynastyTheme.utils.getComponent('button', 'primary', 'sm')
                  : dynastyTheme.utils.getComponent('button', 'ghost', 'sm')
              }`}
            >
              Hitters
            </button>
            <button
              onClick={() => {
                setViewMode('pitching');
                setPositionFilter('all');
              }}
              className={`px-4 py-2 ${
                viewMode === 'pitching'
                  ? dynastyTheme.utils.getComponent('button', 'primary', 'sm')
                  : dynastyTheme.utils.getComponent('button', 'ghost', 'sm')
              }`}
            >
              Pitchers
            </button>
          </div>

          <div className="relative">
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className={`${dynastyTheme.components.input} pr-8`}
            >
              <option value="all">All Positions</option>
              {positions[viewMode].map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>

          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search players..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className={`${dynastyTheme.components.input} w-full pl-8`}
            />
            <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>

        <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} mb-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={dynastyTheme.classes.text.neutralLight}>
              Showing {getFilteredPrices().length} of {generatedPrices?.prices?.length || 0} players
            </span>
            {/* Year Selection Tabs */}
            <div className="flex border rounded-lg">
              <button
                onClick={() => setSelectedYear('previous')}
                className={`px-4 py-1 text-sm ${
                  selectedYear === 'previous'
                    ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark} font-semibold`
                    : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
                }`}
              >
                {previousSeasonYear}
              </button>
              <button
                onClick={() => setSelectedYear('current')}
                className={`px-4 py-1 text-sm ${
                  selectedYear === 'current'
                    ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark} font-semibold`
                    : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
                }`}
              >
                {currentSeasonYear}
              </button>
            </div>
          </div>
          {generatedPrices?.pricing_year && (
            <div className="text-xs text-center">
              <span className={dynastyTheme.classes.text.neutralLight}>
                Pricing based on {generatedPrices.pricing_year} season
                {generatedPrices.current_season && generatedPrices.pricing_year !== generatedPrices.current_season && (
                  <span className="ml-1">
                    (Current season: {generatedPrices.current_season})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        <DynastyTable
          tableType="player-list"
          data={getFilteredPrices()}
          columns={columns}
          maxHeight="500px"
          initialSort={{ key: 'salary', direction: 'desc' }}
          rowsPerItem={1}
          rowTypes={['season']}
        />
      </div>
    </div>
  );
};

export default PriceEditor;